import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import Fuse from "fuse.js";
import { UBICACIONES } from "../utils/ubicaciones";

const TYPE_LABEL = {
  marca: "Marca",
  modelo: "Modelo",
  ubicacion: "Ubicación",
  anio: "Año",
  lote: "Lote",
};

// Ubicación outranks modelo: a location only appears on a direct name match,
// while the model list is fuzzy — an exact hit shouldn't get pushed out of the
// 8-slot dropdown by fuzzy guesses.
const TYPE_ORDER = { marca: 0, ubicacion: 1, modelo: 2, anio: 3, lote: 4 };

const YEAR_PATTERN = /^\d{4}$/;
const LOTE_PATTERN = /^\d{6,}$/;

// Accent-insensitive so "tránsito" still matches the canonical "Transito".
const normalize = (s) =>
  s
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

// Locations are up to two words ("Puerto Cortez", "San Lorenzo") — try the
// longest token window first so both words are consumed as one match.
const findLocationAt = (tokens, start) => {
  for (let n = Math.min(2, tokens.length - start); n > 0; n--) {
    const candidate = tokens.slice(start, start + n).join(" ");
    const ubicacion = UBICACIONES.find((u) => normalize(u) === candidate);
    if (ubicacion) return { ubicacion, consumed: n };
  }
  return null;
};

function SearchBar({
  marcas = [],
  allmodelos = [],
  onCommit,
  onSearch,
  onShowAll,
  placeholder = "Marca, modelo, año, lote, ubicación...",
  className = "",
}) {
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const searchWrapRef = useRef(null);
  const inputRef = useRef(null);
  const suggestionListRef = useRef(null);

  const navigate = useNavigate();

  const fuseBrands = useMemo(
    () => new Fuse(marcas, { threshold: 0.4, includeScore: true }),
    [marcas]
  );

  const fuseModels = useMemo(
    () => new Fuse(allmodelos, { threshold: 0.4, includeScore: true }),
    [allmodelos]
  );

  const findBrandPrefix = useCallback(
    (tokens) => {
      const maxWords = Math.min(tokens.length, 3);
      for (let i = maxWords; i > 0; i--) {
        const candidate = tokens.slice(0, i).join(" ");
        const brand = marcas.find((m) => normalize(m) === candidate);
        if (brand) return { brand, consumed: i };
      }
      return null;
    },
    [marcas]
  );

  const buildSuggestions = useCallback(
    (raw) => {
      const q = raw.trim();
      if (!q) return [];

      const seen = new Set();
      const results = [];
      const add = (item) => {
        const key = `${item.type}:${item.value}`;
        if (seen.has(key)) return;
        seen.add(key);
        results.push(item);
      };

      const lower = normalize(q);
      const tokens = lower.split(/\s+/).filter(Boolean);

      if (YEAR_PATTERN.test(q)) add({ type: "anio", value: q, label: q });
      if (LOTE_PATTERN.test(q)) add({ type: "lote", value: q, label: q });

      const brandPrefix = findBrandPrefix(tokens);
      if (brandPrefix) {
        add({ type: "marca", value: brandPrefix.brand, label: brandPrefix.brand });
      }

      const locQuery = tokens[tokens.length - 1] || lower;
      if (locQuery.length >= 2) {
        UBICACIONES.forEach((u) => {
          const n = normalize(u);
          if (n.includes(locQuery) || n.startsWith(lower)) {
            add({ type: "ubicacion", value: u, label: u });
          }
        });
      }

      const brandQuery = tokens[tokens.length - 1] || lower;
      fuseBrands.search(brandQuery).slice(0, 5).forEach((r) => {
        add({ type: "marca", value: r.item, label: r.item });
      });

      fuseModels.search(lower).slice(0, 4).forEach((r) => {
        add({ type: "modelo", value: r.item, label: r.item });
      });
      if (tokens.length > 1) {
        fuseModels.search(tokens[tokens.length - 1]).slice(0, 3).forEach((r) => {
          add({ type: "modelo", value: r.item, label: r.item });
        });
      }

      results.sort((a, b) => TYPE_ORDER[a.type] - TYPE_ORDER[b.type]);
      return results.slice(0, 8);
    },
    [fuseBrands, fuseModels, findBrandPrefix]
  );

  const parseRawInput = useCallback(
    (raw) => {
      const q = raw.trim();
      if (!q) return [];

      const seen = new Set();
      const matches = [];
      const add = (m) => {
        const key = `${m.type}:${m.value}`;
        if (seen.has(key)) return;
        seen.add(key);
        matches.push(m);
      };

      const lower = normalize(q);
      const tokens = lower.split(/\s+/).filter(Boolean);

      let remaining = tokens;
      const brandPrefix = findBrandPrefix(tokens);
      if (brandPrefix) {
        add({ type: "marca", value: brandPrefix.brand });
        remaining = tokens.slice(brandPrefix.consumed);
      }

      for (let i = 0; i < remaining.length; ) {
        const loc = findLocationAt(remaining, i);
        if (loc) {
          add({ type: "ubicacion", value: loc.ubicacion });
          i += loc.consumed;
          continue;
        }
        const part = remaining[i];
        i += 1;
        if (YEAR_PATTERN.test(part)) {
          add({ type: "anio", value: part });
          continue;
        }
        if (LOTE_PATTERN.test(part)) {
          add({ type: "lote", value: part });
          continue;
        }
        const brand = marcas.find((m) => normalize(m) === part);
        if (brand) {
          add({ type: "marca", value: brand });
          continue;
        }
        const model = allmodelos.find((m) => normalize(m) === part);
        if (model) add({ type: "modelo", value: model });
      }

      if (matches.length === 0) {
        // A typed prefix of a location ("teguci" → Tegucigalpa) beats the
        // fuzzy brand/model guesses.
        const locHit = UBICACIONES.find((u) => normalize(u).startsWith(lower));
        if (locHit) add({ type: "ubicacion", value: locHit });
        if (matches.length === 0) {
          const brandHit = fuseBrands.search(lower)[0];
          if (brandHit) add({ type: "marca", value: brandHit.item });
        }
        if (matches.length === 0) {
          const modelHit = fuseModels.search(lower)[0];
          if (modelHit) add({ type: "modelo", value: modelHit.item });
        }
      }

      return matches;
    },
    [marcas, allmodelos, fuseBrands, fuseModels, findBrandPrefix]
  );

  const handleChange = (event) => {
    const value = event.target.value;
    setInput(value);
    const next = buildSuggestions(value);
    setSuggestions(next);
    setShowSuggestions(next.length > 0);
    setHighlightedIndex(-1);
  };

  const commitSearch = (explicitItem) => {
    const picked =
      explicitItem ||
      (highlightedIndex >= 0 ? suggestions[highlightedIndex] : null);

    const seen = new Set();
    const matches = [];
    const add = (type, value) => {
      const key = `${type}:${value}`;
      if (seen.has(key)) return;
      seen.add(key);
      matches.push({ type, value });
    };

    if (picked) add(picked.type, picked.value);
    parseRawInput(input).forEach((p) => add(p.type, p.value));

    setShowSuggestions(false);
    setSuggestions([]);
    setHighlightedIndex(-1);
    if (onCommit) onCommit();

    // In-place mode (e.g. /subastas): hand the parsed matches + raw text to the
    // caller to filter client-side instead of navigating to the /stock results.
    if (onSearch) {
      onSearch(matches, input.trim());
      return;
    }

    const queryParams = new URLSearchParams();
    matches.forEach((m) => queryParams.append(m.type, m.value));
    navigate(matches.length ? `/stock?${queryParams.toString()}` : "/stock");
  };

  const handleSuggestionClick = (item) => {
    commitSearch(item);
  };

  const handleKeyDown = (event) => {
    if (showSuggestions && suggestions.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightedIndex((i) => (i + 1) % suggestions.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedIndex((i) =>
          i <= 0 ? suggestions.length - 1 : i - 1
        );
        return;
      }
      if (event.key === "Escape") {
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        return;
      }
    }
    if (event.key === "Enter") {
      event.preventDefault();
      commitSearch();
    }
  };

  const handleClear = () => {
    setInput("");
    setSuggestions([]);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    if (suggestions.length > 0) setShowSuggestions(true);
  };

  useEffect(() => {
    const handler = (event) => {
      if (
        searchWrapRef.current &&
        !searchWrapRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (highlightedIndex < 0 || !suggestionListRef.current) return;
    const item = suggestionListRef.current.children[highlightedIndex];
    if (item) item.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  return (
    <div className={`stock-search ${className}`} ref={searchWrapRef}>
      <div
        className={`stock-search-input-wrap ${
          showSuggestions ? "show-suggestions" : ""
        }`}
      >
        <input
          ref={inputRef}
          type="text"
          className="stock-search-input"
          value={input}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showSuggestions}
          aria-controls="stock-search-listbox"
        />
        {input && (
          <button
            type="button"
            className="stock-search-clear"
            onClick={handleClear}
            aria-label="Limpiar búsqueda"
          >
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="6" y1="18" x2="18" y2="6" />
            </svg>
          </button>
        )}
        {showSuggestions && suggestions.length > 0 && (
          <ul
            id="stock-search-listbox"
            ref={suggestionListRef}
            className="stock-search-suggestions"
            role="listbox"
          >
            {suggestions.map((item, index) => (
              <li
                key={`${item.type}-${item.value}-${index}`}
                className={`stock-search-suggestion-item ${
                  highlightedIndex === index ? "is-highlighted" : ""
                }`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  handleSuggestionClick(item);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                role="option"
                aria-selected={highlightedIndex === index}
              >
                <span
                  className={`stock-search-suggestion-type stock-search-type-${item.type}`}
                >
                  {TYPE_LABEL[item.type]}
                </span>
                <span className="stock-search-suggestion-label">
                  {item.label}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {onShowAll ? (
        <button
          type="button"
          className="stock-search-all"
          onClick={() => {
            handleClear();
            onShowAll();
          }}
        >
          Ver todos
        </button>
      ) : (
        <Link to="/stock" className="stock-search-all">
          Ver todos
        </Link>
      )}
      <button
        type="button"
        className="stock-search-btn"
        onClick={() => commitSearch()}
      >
        Buscar
      </button>
    </div>
  );
}

export default SearchBar;
